import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, from, fromEvent, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { OrganizationService } from '../../app/services/organization.service';
import { PerformanceDetailsService } from '../../app/services/performance-details.service';
import { ProgramService } from '../../app/services/program.service';
import { isSelectedOrganizationFilters, isSelectedProgramFilters, selectedFiltersFromOrganizations, selectedFiltersFromPrograms } from '../../models/filters.model';
import { Role } from '../../models/role.model';
import { User } from '../../models/user.model';
import * as PDActions from '../actions/pd.actions';
import { filtersToQueryParams } from '../../app/shared/utils/filter-query-params';

@Injectable()
export class PDEffects {
  private actions$ = inject(Actions);
  private programService = inject(ProgramService);
  private performanceDetailsService = inject(PerformanceDetailsService);
  private organizationService = inject(OrganizationService);
  private router = inject(Router);

  loadUserFromWindowEvent$ = createEffect(() =>
    fromEvent<CustomEvent>(window, 'user').pipe(
      map(e => e.detail?.user as User),
      filter(user => !!user),
      map(user => PDActions.userReceived({ user }))
    )
  );

  loadRolesFromWindowEvent$ = createEffect(() =>
    fromEvent<CustomEvent<{ userRoles: any[] }>>(window, 'userRoles').pipe(
      map(event => (event.detail?.userRoles ?? []).map(r => ({ id: r.id, businessRole: r.businessRole, entityIds: r.entityIds }) as Role)),
      map(roles => PDActions.rolesReceived({ roles }))
    )
  );

  loadPrograms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadPrograms),
      switchMap(() =>
        from(this.programService.getPrograms()).pipe(
          switchMap(programs => {
            const qp = this.router.parseUrl(this.router.url).queryParams;
            const defaultFilters = selectedFiltersFromPrograms(programs, { id: qp['program'], range: qp['range'], from: qp['from'], to: qp['to'] });
            const actions: ReturnType<typeof PDActions.programsLoaded | typeof PDActions.loadProductsForProgram>[] = [
              PDActions.programsLoaded({ programs, defaultFilters }),
            ];
            if (defaultFilters) {
              actions.push(PDActions.loadProductsForProgram({ programId: defaultFilters.program.id }));
            }
            return from(actions);
          }),
          catchError(error => {
            console.error('Error loading programs:', error);
            return of(PDActions.loadProgramsError({ error }));
          })
        )
      )
    )
  );

  loadSitesForOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadSitesForOrganization),
      switchMap(({ organizationId }) =>
        from(this.organizationService.getSitesByOrg(organizationId)).pipe(
          map(sites => PDActions.sitesForOrganizationLoaded({ organizationId, sites })),
          catchError(error => {
            console.error('Error loading sites for organization:', error);
            return of(PDActions.loadSitesForOrganizationError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadProductsForProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadProductsForProgram),
      switchMap(({ programId }) =>
        from(this.programService.getProductsByProgram(programId)).pipe(
          map(products => PDActions.productsForProgramLoaded({ programId, products })),
          catchError(error => {
            console.error('Error loading products for program:', error);
            return of(PDActions.loadProductsForProgramError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  reloadSitePerformancesOnOrgFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.setFilters),
      switchMap(({ filters }) => {
        if (!isSelectedOrganizationFilters(filters)) return EMPTY;
        if (filters.sites.length > 0) {
          return of(
            PDActions.loadSitePerformanceEvents({
              siteIds: filters.sites.map(s => s.id),
              startDttm: filters.dateRange.start.toISOString(),
              endDttm: filters.dateRange.end.toISOString(),
              organization: { id: filters.organization.id, name: filters.organization.name },
            })
          );
        }
        return EMPTY;
      })
    )
  );

  reloadProductPerformancesOnProgramFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.setFilters),
      switchMap(({ filters }) => {
        if (!isSelectedProgramFilters(filters)) return EMPTY;
        const ids = filters.products.map(p => p.id);
        if (ids.length > 0) {
          return of(
            PDActions.loadProductPerformanceEvents({
              productIds: ids,
              startDttm: filters.dateRange.start.toISOString(),
              endDttm: filters.dateRange.end.toISOString(),
              program: { id: filters.program.id, name: filters.program.name },
            })
          );
        }
        return EMPTY;
      })
    )
  );

  loadProductPerformances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadProductPerformanceEvents),
      switchMap(({ productIds, startDttm, endDttm, program }) =>
        from(this.performanceDetailsService.getProductPerformancesByIds(productIds, startDttm, endDttm)).pipe(
          map(results => results.map(e => ({ ...e, programName: e.programName || program.name }))),
          map(performances => PDActions.productPerformanceEventsLoaded({ performances })),
          catchError(error => {
            console.error('Error loading product performances:', error);
            return of(PDActions.loadProductPerformanceEventsError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadSitePerformances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadSitePerformanceEvents),
      switchMap(({ siteIds, startDttm, endDttm, organization }) =>
        from(this.performanceDetailsService.getSitePerformancesByIds(siteIds, startDttm, endDttm)).pipe(
          map(results => results.map(e => ({ ...e, organizationId: organization.id, organizationName: organization.name }))),
          map(performances => PDActions.sitePerformanceEventsLoaded({ performances })),
          catchError(error => {
            console.error('Error loading site performances:', error);
            return of(PDActions.loadSitePerformanceEventsError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadOrganizations),
      switchMap(() =>
        from(this.organizationService.getOrganizations()).pipe(
          switchMap(orgs => {
            const qp = this.router.parseUrl(this.router.url).queryParams;
            const defaultFilters = selectedFiltersFromOrganizations(orgs, { id: qp['org'], range: qp['range'], from: qp['from'], to: qp['to'] });
            const actions: ReturnType<typeof PDActions.organizationsLoaded | typeof PDActions.loadSitesForOrganization>[] = [
              PDActions.organizationsLoaded({ organizations: orgs, defaultFilters }),
            ];
            if (defaultFilters) {
              actions.push(PDActions.loadSitesForOrganization({ organizationId: defaultFilters.organization.id }));
            }
            return from(actions);
          }),
          catchError(error => {
            console.error('Error loading organizations:', error);
            return of(PDActions.loadOrganizationsError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadSitePerformanceIntervalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadSitePerformanceIntervalData),
      switchMap(({ siteId, eventId }) =>
        from(this.performanceDetailsService.getSiteIntervalData(siteId, eventId)).pipe(
          map(data => PDActions.sitePerformanceIntervalDataLoaded({ performanceData: data })),
          catchError(error => {
            console.error('Error loading site performance interval data:', error);
            return of(PDActions.loadSitePerformanceIntervalDataError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadProductPerformanceIntervalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadProductPerformanceIntervalData),
      switchMap(({ productId, eventId }) =>
        from(this.performanceDetailsService.getProductIntervalData(productId, eventId)).pipe(
          map(data => PDActions.sitePerformanceIntervalDataLoaded({ performanceData: data })),
          catchError(error => {
            console.error('Error loading product performance interval data:', error);
            return of(PDActions.loadSitePerformanceIntervalDataError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  loadProductSitePerformances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PDActions.loadProductSitePerformances),
      switchMap(({ productId, eventId }) =>
        from(this.performanceDetailsService.getProductSitePerformances(productId, eventId)).pipe(
          map(performances => PDActions.productSitePerformancesLoaded({ performances })),
          catchError(error => {
            console.error('Error loading product site performances:', error);
            return of(PDActions.loadProductSitePerformancesError({ error: error?.message ?? String(error) }));
          })
        )
      )
    )
  );

  syncFiltersToUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PDActions.setFilters),
        tap(({ filters }) =>
          this.router.navigate([], {
            queryParams: filtersToQueryParams(filters),
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
        )
      ),
    { dispatch: false }
  );
}
